// eslint-disable-next-line import/no-named-as-default
import Icon from "@mdi/react";
import { DragEvent as ReactDragEvent, ReactNode, memo, useState } from "react";

import { mdiDragVertical } from "@mdi/js";

/**
 * Props for DraggableHeader.
 *
 * columnKey  — numeric column index as a string (matches column.key cast to string).
 *              Carried via the "text/column-key" MIME type on the DataTransfer object.
 * name       — display label shown in the header.
 * color      — optional hex color; applied as text + faint background tint (matches
 *              existing HeaderRenderer colour treatment).
 * frozen     — when true, drag source is suppressed; the element is still a valid
 *              drop target suppression to prevent dropping onto frozen columns.
 * onReorder  — called with (fromKey, toKey) when a valid drop occurs.
 * children   — optional extra content (e.g. copy button) rendered after the name.
 */
export interface DraggableHeaderProps {
  columnKey: string;
  name: string;
  color?: string;
  frozen?: boolean;
  onReorder: (fromKey: string, toKey: string) => void;
  children?: ReactNode;
}

/**
 * EVA-882: Draggable column-header wrapper.
 *
 * Integrates with the existing HeaderRenderer in DataTable.tsx:
 * - Accepts drag from non-frozen columns via "text/column-key" MIME.
 * - Shows a blue left-inset drop indicator while dragged over.
 * - Sets source opacity to 0.4 during drag for visual feedback.
 * - Frozen columns (key 0, "group-membership") pass frozen=true to suppress
 *   both drag source and drop target behaviour.
 * - Accepts `children` for extra header content (e.g. copy button).
 *
 * Does NOT replace the POC's HeaderContainer — EVA's existing HeaderContainer
 * styled component in DataTable.tsx is used for frozen columns unchanged.
 * Non-frozen columns render this component instead, carrying the same colour
 * treatment and copy button as children.
 */
export const DraggableHeader = memo(function DraggableHeader({
  columnKey,
  name,
  color,
  frozen,
  onReorder,
  children
}: DraggableHeaderProps) {
  const [isDragOver, setIsDragOver] = useState(false);

  const handleDragStart = frozen
    ? undefined
    : (e: ReactDragEvent<HTMLDivElement>) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/column-key", columnKey);
        (e.currentTarget as HTMLElement).style.opacity = "0.4";
      };

  const handleDragEnd = (e: ReactDragEvent<HTMLDivElement>) => {
    (e.currentTarget as HTMLElement).style.opacity = "1";
    setIsDragOver(false);
  };

  const handleDragOver = frozen
    ? undefined
    : (e: ReactDragEvent<HTMLDivElement>) => {
        // Only accept drops that originated from another column header.
        // Badge drags use "text/badge-key" and must not trigger column reorder.
        if (!e.dataTransfer.types.includes("text/column-key")) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setIsDragOver(true);
      };

  const handleDragLeave = () => setIsDragOver(false);

  const handleDrop = frozen
    ? undefined
    : (e: ReactDragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setIsDragOver(false);
        const fromKey = e.dataTransfer.getData("text/column-key");
        if (fromKey && fromKey !== columnKey) {
          onReorder(fromKey, columnKey);
        }
      };

  return (
    <div
      draggable={!frozen}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      title={name}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%",
        height: "100%",
        cursor: frozen ? "default" : "grab",
        color: color,
        backgroundColor: color ? color + "20" : undefined,
        overflow: "hidden",
        textOverflow: "ellipsis",
        textIndent: "5px",
        // Bleed the tint edge-to-edge over the RDG header-cell padding (mirrors
        // the frozen HeaderContainer), so there's no white gap between columns.
        position: "fixed",
        left: "0px",
        right: "0px",
        boxShadow: isDragOver ? "inset 3px 0 0 #1890ff" : "none"
      }}>
      {!frozen && (
        // Drag affordance — a grip icon signals the header is reorderable
        // (the whole header also shows a `grab` cursor). Solid medium-grey so it
        // reads clearly without the washed-out "white outline" look.
        <Icon
          path={mdiDragVertical}
          size={1}
          color="rgba(0, 0, 0, 0.5)"
          aria-label="Drag to reorder column"
          style={{ flexShrink: 0, marginLeft: -4, marginRight: 2 }}
        />
      )}
      <span
        style={{
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          flex: 1
        }}>
        {name}
      </span>
      {children}
    </div>
  );
});
